import type {
  AvatarList,
  BetLoopProfile,
  BetLoopSharedBet,
  BetLoopStats,
} from '@core/Areas/BetLoop/types'
import { BetLoopFeedPageSize } from '@core/Areas/BetLoop/constants'
import type {
  CreateProfileResponse,
  BetLoopFeedParams,
  BetLoopSearchProfileParams,
  BetLoopPostMultipleSharesResponse,
  UpdateProfile,
  BetLoopPostMultipleKambiSharesResponse,
} from './types'
import { postToBetLoopApi, getFromBetLoopApi, putToBetLoopApi } from './helpers/apiServiceWrapper'
import { store } from '@core/Store'
import {
  selectActiveKambiJurisdiction,
  selectPlayerSessionId,
} from '@core/Areas/KambiClient/Store/selectors'

const enum LocalConstants {
  BetLoopV1ApiBase = '/api/followme/v1',
}

export const postShareToBetLoop = ({ ticketSequence }: { ticketSequence: number }) =>
  postToBetLoopApi<{ postId: number }>({
    url: `${LocalConstants.BetLoopV1ApiBase}/share/bet`,
    body: { ticketSequence },
  })

export const postSportsShareToBetLoop = ({
  couponRef,
  betRef,
  kambiBearer,
}: {
  couponRef: number
  betRef: number
  kambiBearer: string
}) => {
  const offering = selectActiveKambiJurisdiction(store.getState())
  return postToBetLoopApi<{ postId: number }>({
    url: `${LocalConstants.BetLoopV1ApiBase}/share/sport/bet`,
    body: { couponRef, betRef, offering },
    additionalHeaders: {
      'X-Kambi-Bearer': kambiBearer,
    },
  })
}

export const postMultipleSharesToBetLoop = ({ ticketSequences }: { ticketSequences: number[] }) =>
  postToBetLoopApi<BetLoopPostMultipleSharesResponse>({
    url: `${LocalConstants.BetLoopV1ApiBase}/share/bets`,
    body: { ticketSequences },
  })

export const postMultipleKambiSharesToBetLoop = ({
  bets,
}: {
  bets: { couponRef: number; betRef: number; offering?: 'rwwawa' | 'rwwasa' | null }[]
}) => {
  const kambiBearer = selectPlayerSessionId(store.getState())

  if (!kambiBearer) {
    throw new Error('Kambi: Not authorised to share to bet loop')
  }
  const offering = selectActiveKambiJurisdiction(store.getState())
  for (const bet of bets) {
    bet.offering = offering
  }
  return postToBetLoopApi<BetLoopPostMultipleKambiSharesResponse>({
    url: `${LocalConstants.BetLoopV1ApiBase}/share/sport/bets`,
    body: { bets },
    additionalHeaders: {
      'X-Kambi-Bearer': kambiBearer,
    },
  })
}

export const postCreateBetLoopProfile = () =>
  postToBetLoopApi<CreateProfileResponse>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/`,
    body: {},
  })

export const getBetLoopSharedBetsFeed = async ({
  pageParam = null,
  sortOrder = 'sortAsc',
  sortFilter = 'JumpTime',
  feedType = 'explore',
  accountId = null,
  fromPostId = undefined,
  pageSize = BetLoopFeedPageSize,
  type = 'racing',
}: BetLoopFeedParams) => {
  const params = new URLSearchParams()
  params.append('pageSize', (pageSize ?? BetLoopFeedPageSize).toString())

  const isFollowing = feedType === 'following'

  params.append('following', `${isFollowing}`)

  if (accountId !== null && (feedType === 'pending' || feedType === 'resulted')) {
    params.append('account', accountId)
  }

  if (sortOrder === 'sortAsc') {
    params.append('sortAsc', sortFilter)
  } else {
    params.append('sortDesc', sortFilter)
  }

  if (fromPostId) {
    params.append('from', fromPostId.toString())
    params.append('newPosts', 'true')
  } else if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  if (type) {
    params.append('type', type)
  }

  const url =
    feedType !== 'resulted'
      ? `${LocalConstants.BetLoopV1ApiBase}/share/feed?${params.toString()}`
      : `${LocalConstants.BetLoopV1ApiBase}/share/resulted?${params.toString()}`

  return getFromBetLoopApi<BetLoopSharedBet[]>({ url })
}

export const getCopiedBetsFeed = async ({ pageParam = null }: { pageParam?: number | null }) => {
  const params = new URLSearchParams()
  params.append('pageSize', BetLoopFeedPageSize.toString())
  if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  const url = `${LocalConstants.BetLoopV1ApiBase}/share/copied?${params.toString()}`
  return getFromBetLoopApi<BetLoopSharedBet[]>({ url })
}

export const getPrincipleBetLoopUserProfile = () => {
  return getFromBetLoopApi<BetLoopProfile>({ url: `${LocalConstants.BetLoopV1ApiBase}/account` })
}

export const getPrincipleBetLoopUserProfileStats = (accountId: string) => {
  return getFromBetLoopApi<BetLoopStats>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/${accountId}/stats`,
  })
}

export const getBetLoopProfileFollowAccountsPaged = async ({
  pageParam = null,
  accountId = undefined,
  isFollowing = true,
}: {
  pageParam: string | null
  accountId: string | undefined
  isFollowing?: boolean
}) => {
  const params = new URLSearchParams()
  if (pageParam) {
    params.append('from', pageParam.toString())
  }
  if (accountId) {
    params.append('accountId', accountId)
  }
  params.append('pageSize', BetLoopFeedPageSize.toString())
  const accountFollowUri = isFollowing ? 'following' : 'followed'
  const url = `${LocalConstants.BetLoopV1ApiBase}/account/${accountFollowUri}?${params.toString()}`
  return getFromBetLoopApi<BetLoopProfile[]>({ url })
}

export function postBetLoopFollowAccount(accountId: string) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/follow`,
    body: { followsAccountId: accountId },
  })
}

export function postBetLoopUnfollowAccount(accountId: string) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/unfollow`,
    body: { accountToUnfollowId: accountId },
  })
}

export function postBetLoopReportProfile(accountId: string) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/report`,
    body: { accountId },
  })
}

export function postBetLoopNotifications(accountId: string, shouldNotify: boolean) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/notification/set`,
    body: { followedAccountId: accountId, notify: shouldNotify },
  })
}

export const getBetLoopUserProfileById = (id: string) =>
  getFromBetLoopApi<BetLoopProfile>(
    { url: `${LocalConstants.BetLoopV1ApiBase}/account/${id}` },
    { skipGlobalBanCheck: true }
  )

export const getBetLoopAvatarList = async () => {
  return await getFromBetLoopApi<AvatarList[]>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account/avatars`,
  })
}

export const putBetLoopProfileDetails = (body: UpdateProfile) => {
  return putToBetLoopApi<UpdateProfile>({
    url: `${LocalConstants.BetLoopV1ApiBase}/account`,
    body,
  })
}

export const getBetLoopSharedBetByPostId = (postId: number) =>
  getFromBetLoopApi<BetLoopSharedBet>({
    url: `${LocalConstants.BetLoopV1ApiBase}/share/post/${postId}`,
  })

export const getBetLoopCanShareTickets = (
  ticketSequences: string[],
  betRefPairs: { couponRef: number; betRef: number }[]
) =>
  postToBetLoopApi<{ canShare: boolean; ticketSequence: number; betRef: number }[]>({
    url: `${LocalConstants.BetLoopV1ApiBase}/share/tickets/can-share`,
    body: { ticketSequences, betRefPairs },
  })

export const getBetLoopSearchProfiles = async ({
  pageParam = undefined,
  pageSize = BetLoopFeedPageSize,
  searchQuery = undefined,
  sortOrder = undefined,
}: BetLoopSearchProfileParams) => {
  const params = new URLSearchParams()

  if (sortOrder !== undefined) {
    params.append('sortOrder', sortOrder.toString())
  }

  params.append('pageSize', (pageSize ?? BetLoopFeedPageSize).toString())
  if (pageParam !== undefined) {
    params.append('from', pageParam.toString())
  }
  if (searchQuery !== undefined && searchQuery.length >= 2) {
    params.append('search', searchQuery.toString())
  }

  const url = `${LocalConstants.BetLoopV1ApiBase}/account/list?${params.toString()}`
  return getFromBetLoopApi<BetLoopProfile[]>({ url })
}
